import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Privacy Policy | Savi Coliving</title>
        <meta name="description" content="Privacy policy for Savi Coliving. Learn how we collect, use, and protect your personal data under GDPR." />
        <link rel="canonical" href="https://www.savicoliving.com/privacy" />
      </Helmet>

      <PublicNav />

      <div className="pt-24 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-2">Privacy Policy</h1>
          <p className="text-[#2E2A26]/40 text-sm mb-10">Last updated: April 2026</p>

          <div className="prose prose-stone max-w-none space-y-8">
            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">1. Who We Are</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
                Savi Coliving is operated by Steve Haynes, located in Naquera, Valencia, Spain. We provide coliving accommodation and community services for founders, creatives, and remote workers. For any privacy-related inquiries, contact us at <a href="mailto:hola@savicoliving.com" className="text-[#C85A38]">hola@savicoliving.com</a>.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">2. What Data We Collect</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-3">We collect the following categories of personal data:</p>
              <ul className="list-disc pl-5 text-[#2E2A26]/60 text-sm space-y-1.5">
                <li><strong className="text-[#2E2A26]/80">Application data:</strong> Name, email address, phone number, profession, and any information you provide in your application.</li>
                <li><strong className="text-[#2E2A26]/80">Contract data:</strong> Identity documents, billing address, and payment details required for your stay.</li>
                <li><strong className="text-[#2E2A26]/80">Usage data:</strong> Pages visited, device information, and interaction data (only with your consent via cookies).</li>
                <li><strong className="text-[#2E2A26]/80">Communication data:</strong> Messages sent via WhatsApp, email, or contact forms.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">3. Legal Basis for Processing (GDPR)</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-3">We process your data based on:</p>
              <ul className="list-disc pl-5 text-[#2E2A26]/60 text-sm space-y-1.5">
                <li><strong className="text-[#2E2A26]/80">Consent:</strong> For analytics cookies and marketing communications. You can withdraw consent at any time.</li>
                <li><strong className="text-[#2E2A26]/80">Contract:</strong> To process your application, manage your stay, and handle payments.</li>
                <li><strong className="text-[#2E2A26]/80">Legitimate interest:</strong> To improve our services, ensure security, and respond to inquiries.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">4. How We Use Your Data</h2>
              <ul className="list-disc pl-5 text-[#2E2A26]/60 text-sm space-y-1.5">
                <li>Processing and reviewing your application</li>
                <li>Managing bookings and generating contracts</li>
                <li>Communicating about your stay and community events</li>
                <li>Improving our website and services (with consent)</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">5. Third-Party Services</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-3">We use the following third-party services, which only receive data after your consent:</p>
              <ul className="list-disc pl-5 text-[#2E2A26]/60 text-sm space-y-1.5">
                <li><strong className="text-[#2E2A26]/80">Google Analytics 4:</strong> Website analytics (data processed in the EU/US under Standard Contractual Clauses).</li>
                <li><strong className="text-[#2E2A26]/80">PostHog:</strong> Product analytics and session recording (US-based, with SCCs).</li>
                <li><strong className="text-[#2E2A26]/80">Google Apps Script:</strong> For processing contact form submissions and contract generation.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">6. Data Retention</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
                We retain application data for up to 24 months after your inquiry. Guest data is retained for the duration of your stay plus 5 years for legal and tax obligations. Analytics data is automatically anonymised after 14 months. You may request deletion at any time.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">7. Your Rights (GDPR)</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-3">Under the General Data Protection Regulation, you have the right to:</p>
              <ul className="list-disc pl-5 text-[#2E2A26]/60 text-sm space-y-1.5">
                <li><strong className="text-[#2E2A26]/80">Access:</strong> Request a copy of your personal data.</li>
                <li><strong className="text-[#2E2A26]/80">Rectification:</strong> Correct inaccurate or incomplete data.</li>
                <li><strong className="text-[#2E2A26]/80">Erasure:</strong> Request deletion of your data ("right to be forgotten").</li>
                <li><strong className="text-[#2E2A26]/80">Restriction:</strong> Limit how we process your data.</li>
                <li><strong className="text-[#2E2A26]/80">Portability:</strong> Receive your data in a structured, machine-readable format.</li>
                <li><strong className="text-[#2E2A26]/80">Objection:</strong> Object to processing based on legitimate interest.</li>
                <li><strong className="text-[#2E2A26]/80">Withdraw consent:</strong> At any time, without affecting prior processing.</li>
              </ul>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed mt-3">
                To exercise these rights, email <a href="mailto:hola@savicoliving.com" className="text-[#C85A38]">hola@savicoliving.com</a>. You also have the right to lodge a complaint with the Spanish Data Protection Agency (AEPD) at <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-[#C85A38]">www.aepd.es</a>.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">8. Security</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
                We implement appropriate technical and organisational measures to protect your personal data, including encrypted connections (HTTPS), secure password hashing, and access controls. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">9. Changes to This Policy</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
                We may update this policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this page periodically.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl text-[#2E2A26] mb-3">10. Contact</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
                For any questions about this privacy policy or your personal data, contact us at:<br />
                <strong className="text-[#2E2A26]/80">Email:</strong> <a href="mailto:hola@savicoliving.com" className="text-[#C85A38]">hola@savicoliving.com</a><br />
                <strong className="text-[#2E2A26]/80">WhatsApp:</strong> <a href="https://wa.me/34632628051" className="text-[#C85A38]">+34 632 62 80 51</a><br />
                <strong className="text-[#2E2A26]/80">Address:</strong> Naquera, Valencia, Spain
              </p>
            </section>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
